import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';

/** Sources a person can record. The system records sms_start / inbound_text / import / api. */
export const CONSENT_SOURCES = [
  { value: 'web_form', label: 'Web form' },
  { value: 'vendor', label: 'Lead vendor' },
  { value: 'verbal', label: 'Verbal (call or meeting)' },
  { value: 'written', label: 'Written (signed form, email)' },
  { value: 'other', label: 'Other' },
] as const;
export type PersonConsentSource = (typeof CONSENT_SOURCES)[number]['value'];

const SOURCE_LABELS: Record<string, string> = {
  ...Object.fromEntries(CONSENT_SOURCES.map((s) => [s.value, s.label])),
  sms_start: 'Texted START',
  inbound_text: 'Texted us first',
  import: 'Import',
  api: 'API intake',
};

export function consentSourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

export interface ConsentRecord {
  id: string;
  source: string;
  sourceDetail: string | null;
  scope: string;
  capturedAt: string;
  note: string | null;
  recordedBy: string | null;
  createdAt: string;
}

export function useConsentQuery(workspaceId: string, merchantId: string) {
  return useQuery({
    queryKey: queryKeys.consent(workspaceId, merchantId),
    queryFn: async (): Promise<ConsentRecord[]> => {
      const { data, error } = await supabase()
        .from('consent_records')
        .select(
          'id, source, source_detail, scope, captured_at, note, recorded_by_user_id, created_at',
        )
        .eq('workspace_id', workspaceId)
        .eq('merchant_id', merchantId)
        .order('captured_at', { ascending: false });
      if (error) throw new Error(`Couldn't load consent records: ${error.message}`);
      return data.map((c) => ({
        id: c.id,
        source: c.source,
        sourceDetail: c.source_detail,
        scope: c.scope,
        capturedAt: c.captured_at,
        note: c.note,
        recordedBy: c.recorded_by_user_id,
        createdAt: c.created_at,
      }));
    },
  });
}

export interface NewConsent {
  source: PersonConsentSource;
  sourceDetail: string;
  scope: string;
  /** ISO timestamp. */
  capturedAt: string;
  note: string;
}

/** Records consent (append-only: a record is never edited; record a new one instead). */
export function useRecordConsent(workspaceId: string, merchantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewConsent) => {
      const { error } = await supabase()
        .from('consent_records')
        .insert({
          workspace_id: workspaceId,
          merchant_id: merchantId,
          source: input.source,
          source_detail: input.sourceDetail.trim() || null,
          scope: input.scope.trim(),
          captured_at: input.capturedAt,
          note: input.note.trim() || null,
        });
      if (error) throw new Error(error.message.charAt(0).toUpperCase() + error.message.slice(1));
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.consent(workspaceId, merchantId) }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.merchantTimeline(workspaceId, merchantId),
        }),
      ]);
    },
  });
}
